import React from 'react';
import type { LiveRegionProps } from './types';

export const a11y = {
  announceMessage: (message: string) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.classList.add('sr-only');
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  },

  focusTrap: (element: HTMLElement) => {
    const focusableElements = element.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    let currentFocus: HTMLElement | null = null;

    function handleTabKey(e: KeyboardEvent) {
      const isTabPressed = e.key === 'Tab';
      if (!isTabPressed) return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }

      currentFocus = document.activeElement as HTMLElement;
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        const closeButton = element.querySelector<HTMLElement>('[aria-label="Close"]');
        if (closeButton) {
          e.preventDefault();
          closeButton.click();
        }
      }
    }

    const previousFocus = document.activeElement as HTMLElement;

    element.addEventListener('keydown', handleTabKey);
    element.addEventListener('keydown', handleEscape);
    firstFocusable?.focus();

    return () => {
      element.removeEventListener('keydown', handleTabKey);
      element.removeEventListener('keydown', handleEscape);
      if (previousFocus && document.body.contains(previousFocus)) {
        previousFocus.focus();
      }
    };
  },

  SkipLink: React.memo(function SkipLink() {
    return (
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-dark-text focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400"
      >
        Skip to main content
      </a>
    );
  }),

  visuallyHidden: 'sr-only',

  visuallyHiddenFocusable: 'sr-only focus:not-sr-only',

  hideFromScreenReaders: 'aria-hidden="true"',

  createAriaLabel: (template: string, values: Record<string, string | number>) => {
    return Object.entries(values).reduce(
      (acc, [key, value]) => acc.replace(`{${key}}`, String(value)),
      template
    );
  },

  useFocusReturn: (triggerRef: React.RefObject<HTMLElement>) => {
    const lastFocusedElement = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
      lastFocusedElement.current = document.activeElement as HTMLElement;

      return () => {
        if (
          lastFocusedElement.current &&
          triggerRef.current &&
          document.body.contains(lastFocusedElement.current)
        ) {
          lastFocusedElement.current.focus();
        }
      };
    }, [triggerRef]);
  },

  useModalFocus: (modalRef: React.RefObject<HTMLElement>, isOpen: boolean) => {
    React.useEffect(() => {
      if (isOpen && modalRef.current) {
        const cleanup = a11y.focusTrap(modalRef.current);
        return cleanup;
      }
    }, [isOpen, modalRef]);
  },

  LiveRegion: React.memo(function LiveRegion({ 
    children, 
    ariaLive = 'polite',
    className = '',
    role = 'status'
  }: LiveRegionProps) {
    return (
      <div
        role={role}
        aria-live={ariaLive}
        aria-atomic="true"
        className={`sr-only ${className}`}
      >
        {children}
      </div>
    );
  }),

  useListNavigation: (listRef: React.RefObject<HTMLElement>) => {
    React.useEffect(() => {
      const list = listRef.current;
      if (!list) return;

      const items = Array.from(list.querySelectorAll('[role="listitem"]'));
      let currentIndex = -1;

      function handleKeyDown(e: KeyboardEvent) {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;

        e.preventDefault();

        switch (e.key) {
          case 'ArrowDown':
            currentIndex = Math.min(currentIndex + 1, items.length - 1);
            break;
          case 'ArrowUp':
            currentIndex = Math.max(currentIndex - 1, 0);
            break;
          case 'Home':
            currentIndex = 0;
            break;
          case 'End':
            currentIndex = items.length - 1;
            break;
        }

        (items[currentIndex] as HTMLElement).focus();
      }

      list.addEventListener('keydown', handleKeyDown);
      return () => list.removeEventListener('keydown', handleKeyDown);
    }, [listRef]);
  },

  useAriaAnnouncer: () => {
    const [message, setMessage] = React.useState('');

    React.useEffect(() => {
      if (message) {
        a11y.announceMessage(message);
        setMessage('');
      }
    }, [message]);

    return setMessage;
  },

  usePreventScroll: (isActive: boolean) => {
    React.useEffect(() => {
      if (isActive) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        
        return () => {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        };
      }
    }, [isActive]);
  }
};